import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  IActionMenuItems,
  Table,
  useFetch,
  useNotificationContext,
  UsePrivileges,
} from "@4uhub/lib4uhub";

import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SyncIcon from "@mui/icons-material/Sync";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";

import MainApiService from "../../../../../services/mainApi.service";
import { ITicketsService } from "../../../../../models/tickets-service";
import TicketServWhatsService from "../../../../../services/ticketServiceWhats.service";
import { getTypebotConfigByTicketId } from "../../../../../services/ticketServiceTypeBot.service";
import TicketServiceId from "./TicketServiceId";

const SITE_APP_ROUTE =
  process.env.REACT_APP_TICKETS + "/api/v1/TicketServices/";
const siteAppService = new MainApiService<ITicketsService>(SITE_APP_ROUTE);

const ticketServWhatsService = new TicketServWhatsService();

const translationPath = "chatBot.page.register.ticketService.";

const SiteAppPage = () => {
  const { eI } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { privileges } = UsePrivileges();

  const [refresh, setRefresh] = useState(0);

  const { sendRequest: getTypebotConfig, loading: isFetchingTypebot } =
    useFetch(getTypebotConfigByTicketId);
  const { sendRequest: getWhatsConfig, loading: isFetchingWhatsapp } = useFetch(
    ticketServWhatsService.getWhatsConfig,
  );
  const { sendRequest: syncWhatsConfig } = useFetch(
    ticketServWhatsService.syncWhatsConfig,
  );

  const isSysAdmin = useMemo(
    () => privileges.find(({ name }) => name === "platform-all"),
    [privileges],
  );

  const columns: GridColDef<ITicketsService>[] = [
    {
      field: "name",
      headerName: t(translationPath + "name") || "Name",
    },
    {
      field: "id",
      headerName: t(translationPath + "id") || "Id do Serviço",
      renderCell: (params) => <TicketServiceId data={params.value} />,
    },
    {
      field: "phoneNumber",
      headerName: t(translationPath + "phoneNumber") || "Telefone",
    },
    {
      field: "qualityRating",
      headerName:
        t(translationPath + "qualityRating") || "Classificação de qualidade",
    },
    {
      field: "ticketServiceType",
      renderCell: ({ value: { name } }) => name,
      headerName:
        t(translationPath + "ticket_service_type") || "Ticket Service Type",
    },
  ];

  const handleAdd = () => {
    navigate("new");
  };

  const handleRowClick = (params: GridRowParams<ITicketsService>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const navigateToSettings = useCallback(
    async (
      ticketServiceId: string,
      service: "whatsapp" | "typebot" = "whatsapp",
      ticketMessageReceivedEventCode: string | undefined,
    ) => {
      const request = service === "typebot" ? getTypebotConfig : getWhatsConfig;

      const { data: { id } = {} as any, success } =
        await request(ticketServiceId);

      if (id && success) {
        navigate(
          `${service}Config/${ticketServiceId}/${id}/edit?ticketMessageReceivedEventCode=${ticketMessageReceivedEventCode}`,
        );
      } else {
        navigate(
          `${service}Config/${ticketServiceId}/new?ticketMessageReceivedEventCode=${ticketMessageReceivedEventCode}`,
        );
      }
    },
    [getWhatsConfig, navigate, getTypebotConfig],
  );

  const { setMessage } = useNotificationContext();

  const props = useMemo(() => {
    return {
      refresh: refresh > 0 ? refresh : undefined,
    };
  }, [refresh]);

  const syncWhatsappNumber = useCallback(
    async (ticketServiceId: string) => {
      const request = syncWhatsConfig;
      const { success } = await request(ticketServiceId);
      if (success) {
        setMessage({
          message: t(translationPath + "sync_whats_success"),
        });
        setRefresh((prevState) => prevState + 1);
      } else {
        setMessage({
          message: t(translationPath + "sync_whats_error"),
          type: "error",
        });
      }
    },
    [syncWhatsConfig, setMessage, setRefresh],
  );

  const rowActions = useCallback(
    ({
      row: { ticketServiceType, ticketMessageReceivedEvent } = {} as any,
    }: GridRowParams<ITicketsService>) => {
      let actions: IActionMenuItems<ITicketsService>[] = [];

      if (ticketServiceType.code === "1") {
        actions = actions.concat([
          {
            action: ({
              row: { id } = {} as ITicketsService,
            }: GridRowParams<ITicketsService>) =>
              navigateToSettings(
                id,
                "whatsapp",
                ticketMessageReceivedEvent?.code,
              ),
            text: t(translationPath + "whats_settings"),
            icon: <WhatsAppIcon fontSize="small" />,
          },
          {
            action: ({
              row: { id } = {} as ITicketsService,
            }: GridRowParams<ITicketsService>) => syncWhatsappNumber(id),
            text: t(translationPath + "sync_whats"),
            icon: <SyncIcon fontSize="small" />,
          },
        ]);
      }

      if (
        isSysAdmin &&
        (ticketServiceType.code === "1" || ticketServiceType.code === "3")
      ) {
        actions = actions.concat([
          {
            action: ({
              row: { id } = {} as ITicketsService,
            }: GridRowParams<ITicketsService>) =>
              navigateToSettings(
                id,
                "typebot",
                ticketMessageReceivedEvent?.code,
              ),
            text: t(translationPath + "typebot_settings"),
            icon: <SettingsSuggestIcon fontSize="small" />,
          },
        ]);
      }

      return actions;
    },
    [isSysAdmin, navigateToSettings, t],
  );

  return (
    <Table<ITicketsService>
      showDefaultMacroFilters={false}
      service={siteAppService}
      columns={columns}
      title={t(translationPath + "ticket_service")}
      searchInput
      onAdd={handleAdd}
      onRowClick={handleRowClick}
      showDeleteButton={false}
      backButton
      backButtonUrl={`/e/${eI}/dashboard/settings`}
      syncing={isFetchingWhatsapp || isFetchingTypebot}
      rowActions={rowActions}
      serviceProps={props}
      searchInputPlaceHolder={
        t(translationPath + "search") || "Search for a ticket service"
      }
      addButtonLabel={
        t(translationPath + "add_ticket_service") || "Add Ticket Service"
      }
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default SiteAppPage;
